import { useState } from 'react';
import { useFormik } from 'formik';
import { useToast } from '@chakra-ui/react';
import { ProductAccessRowStateType } from '../../../../common/components/ProductAccessOptions/ProductAccessOptions';
import { createTeamValidationSchema, initialValues } from '../utils';
import { MemberOption } from '../../AddMembersDropdown/types';
import { useCreateGroup, useAddGroupMembers } from '../../../api';
import { useDrawerStateDispatch } from 'features/main/context';
import { closeDrawer } from 'features/main/context/drawer/actionCreators';

// Recursive function to add members in batches of 5
const addMembersRecursively = async (
  addGroupMembersMutation: any,
  groupId: string,
  memberIds: string[],
  batchIndex: number = 0,
): Promise<void> => {
  // Base case: no more members to process
  if (memberIds.length === 0) return;

  const batch = memberIds.slice(0, 5);
  const remaining = memberIds.slice(5);

  try {
    await addGroupMembersMutation.mutateAsync({
      groupId,
      members: batch,
    });

    // Recursive call for remaining members
    return await addMembersRecursively(
      addGroupMembersMutation,
      groupId,
      remaining,
      batchIndex + 1,
    );
  } catch (error) {
    console.error('Batch %d failed:', batchIndex + 1, error);

    // Continue recursively with remaining members even if current batch fails
    return await addMembersRecursively(
      addGroupMembersMutation,
      groupId,
      remaining,
      batchIndex + 1,
    );
  }
};

export const useCreateTeamForm = () => {
  const toast = useToast();
  const [, drawerDispatch] = useDrawerStateDispatch();

  // App access state management - kept separate from formik
  const [productAccessState, setProductAccessState] =
    useState<ProductAccessRowStateType>([]);

  // Members state management
  const [selectedMembers, setSelectedMembers] = useState<MemberOption[]>([]);

  // State to track loading for member addition
  const [isAddingMembers, setIsAddingMembers] = useState(false);

  // Group creation mutation - no callbacks needed since we use mutateAsync
  const createGroupMutation = useCreateGroup();

  // Add group members mutation - no groupId parameter needed now
  const addGroupMembersMutation = useAddGroupMembers();

  const handleCloseDrawer = () => {
    formik.resetForm();
    setProductAccessState([]);
    setSelectedMembers([]);
    drawerDispatch(closeDrawer());
  };

  // Formik setup
  const formik = useFormik({
    initialValues,
    validationSchema: createTeamValidationSchema,
    onSubmit: async (values) => {
      try {
        // Convert productAccessState to roles format
        const roles = productAccessState.map((item) => ({
          scope: item.product.productCode,
          role: item.accessType.value,
          tenantId: item.product.id,
        }));

        const groupPayload = {
          name: values.name,
          description: values.description,
          roles,
        };

        // Step 1: Create the group and get the response
        const response = await createGroupMutation.mutateAsync(groupPayload);
        const groupId = response.data.id;

        // Step 2: Add members to the group if any are selected
        if (selectedMembers.length > 0) {
          setIsAddingMembers(true);

          const memberIds = selectedMembers.map(({ value }) => value);

          try {
            await addMembersRecursively(
              addGroupMembersMutation,
              groupId,
              memberIds,
            );
          } catch (error) {
            console.error('Error in recursive member addition:', error);
          } finally {
            setIsAddingMembers(false);
          }
        }

        // Show success toast
        toast({
          title: 'Group created successfully',
          description: `Group "${values.name}" has been created${
            selectedMembers.length > 0
              ? ` with ${selectedMembers.length} member(s)`
              : ''
          }.`,
          status: 'success',
        });

        // Close drawer and reset form after successful completion
        handleCloseDrawer();
      } catch (error) {
        console.error('Error in form submission:', error);
        setIsAddingMembers(false);

        // Show error toast
        toast({
          title: 'Failed to create group',
          description:
            //@ts-expect-error
            error?.response?.data?.detail ||
            'An error occurred while creating the group. Please try again.',
          status: 'error',
        });

        // Don't reset form on error so user can retry
      }
    },
  });

  const handleMembersSelect = (members: MemberOption[]) => {
    setSelectedMembers(members);
  };

  return {
    // Formik values (matching the exact interface)
    values: formik.values,
    errors: formik.errors,
    touched: formik.touched,
    handleChange: formik.handleChange,
    handleSubmit: formik.handleSubmit,
    handleBlur: formik.handleBlur,
    isSubmitting:
      formik.isSubmitting || createGroupMutation.isLoading || isAddingMembers,
    resetForm: formik.resetForm,

    // Additional functionality
    productAccessState,
    setProductAccessState,
    selectedMembers,
    handleMembersSelect,
    handleCloseDrawer,
  };
};
